import { Trans } from '@lingui/react/macro';

import { GoToCommentsButton } from '@/components/comments/GoToCommentsButton/GoToCommentsButton';
import { Link } from '@/components/Link/Link';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import { useArticleName } from '@/models/article';
import {
  type ArticleWithRelations,
  type RequestWithRelations,
  useComments,
} from '@/models/request';
import {
  PrintItemSheetButton,
  PrintItemSheetMenuItem,
  PrintsMenu,
} from '@/routes/Requests/Request/components/RequestHeader/PrintOptions';

export const RequestBreadcrumb = ({
  request,
  article,
  showItemSheetPreview,
  onCommentButtonPress,
}: {
  request: RequestWithRelations;
  article?: ArticleWithRelations;
  showItemSheetPreview?: boolean;
  onCommentButtonPress?: () => void;
}) => {
  const articleName = useArticleName({ article });

  const { data: { comments } = { comments: [] } } = useComments({
    requestId: request.id,
    articleId: article?.id,
  });

  return (
    <Stack
      row
      gap="0.5rem"
      alignItems="center"
      justifyContent="space-between"
      style={{ minHeight: '2rem' }}
    >
      <Stack row gap="0.5rem" alignItems="center">
        <InlineText color="disabled">
          <Link to={`/requests`} className="reset-link">
            <Trans id="request.breadcrumb.requests">Requests</Trans>
          </Link>
        </InlineText>

        <InlineText color="disabled">&gt;</InlineText>

        {!article && <InlineText>{request.reference}</InlineText>}

        {article && (
          <>
            <InlineText color="disabled">
              <Link to={`/requests/${request.id}`} className="reset-link">
                {request.reference}
              </Link>
            </InlineText>

            <InlineText color="disabled">&gt;</InlineText>

            <InlineText>{articleName}</InlineText>
          </>
        )}
      </Stack>
      <Stack row gap="0.5rem" alignItems="center">
        {article && onCommentButtonPress && (
          <GoToCommentsButton
            onCommentButtonPress={onCommentButtonPress}
            nbOfComments={comments.length}
            size="medium"
            variant="tertiary"
          />
        )}

        {!!article?.serviceChosenAt &&
          !article?.archivedAt &&
          (showItemSheetPreview ? (
            <PrintsMenu variant="tertiary">
              <PrintItemSheetMenuItem article={article} />
              <PrintItemSheetMenuItem article={article} preview />
            </PrintsMenu>
          ) : (
            <PrintItemSheetButton article={article} size="medium" variant="tertiary" iconOnly />
          ))}
      </Stack>
    </Stack>
  );
};
